import RecordIcon from '@/public/icons/record/record.svg';
import StopIcon from '@/public/icons/record/stop.svg';
import PauseIcon from '@/public/icons/record/pause.svg';
import PlayIcon from '@/public/icons/record/play.svg';
import { formatAudioTime2 } from '@/utils/function';
import { CSSProperties, useEffect, useState } from 'react';
import { RecordFilter, Recording } from '@/types/api';
import { useFormContext } from 'react-hook-form';
import { t } from 'i18next';
import { Tooltip } from '@mui/material';

import {
  Container,
  RecordingContainer,
  SpinnerLayout,
  SpinningIcon,
  StyledText,
  StyledTimer,
  StyledTopLayout,
  SvgBoxButton,
} from './styles';
import { useRecorder } from '@/components/RecorderProvider';
import CustomVoiceVisualizer from '@/components/Record/VoiceRecord2/CustomVoiceVisualizer';
import AudioPlaceholder from '@/components/Record/AudioPlaceholder';
import { useSetAudioFile } from '@/hooks/apiHooks';
import { captureRecordError } from '@/components/Record/util/sentry';
import { useSetAtom } from 'jotai';
import { uploadingState } from '@/recoil/atom';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import {
  ATTACH_GIVEN_UP_MESSAGE,
  attachFailedDialogOptions,
} from '@/components/Record/util/attachFailedDialog';

type VoiceRecord2Props = {
  docId: string;
  style?: CSSProperties;
  onFirstStart?: () => void;
};

// 지금 문서에 연결하는 중인 녹음 uuid → 연결 API 약속. 모듈 단위로 둔다.
// 이 부품은 한 화면에 두 번 그려지고(본문 · 하단 바), 같은 문서를 페이지와 알림 모달에서 함께 열면 폼(useForm)도 둘이다.
// 연결 API(PUT)는 먼저 시작한 부품 하나만 보내고, 나머지는 같은 약속을 기다렸다가 자기 폼에만 붙인다 —
// 그냥 건너뛰면 두 번째 폼은 완료를 못 받아 새 녹음이 그 화면 목록에서 빠졌다 (PORTAL-12588 재QA, AI 리뷰 [보통]).
// 폼의 recordings 로 거르는 건 연결이 끝난 뒤에만 통하므로, 시작 시점에 여기서 먼저 가른다
const attachingUuids = new Map<string, Promise<void>>();

const VoiceRecord2 = (props: VoiceRecord2Props) => {
  const { docId, style, onFirstStart } = props;
  const { setValue, getValues } = useFormContext<RecordFilter>();
  const setUploading = useSetAtom(uploadingState);
  const dialog = useDialog();
  const { showError } = useSnackbar();

  const {
    controls,
    isRecordingInProgress,
    start,
    pauseResume,
    stop,
    isPausedRecording,
    state,
    setStateInit,
    recordingTime,
  } = useRecorder();

  const disabled = docId !== state.docId && isRecordingInProgress;
  // 연결이 끝났을 때 폼이 아직 녹음한 문서를 싣고 있는지 본다 — 그사이 다른 문서로 옮겨 갔으면 폼은 그 문서 것이라
  // 건드리지 않는다(새 문서에 붙고 자동 정리까지 돌았다 — PORTAL-12588).
  // 회의록 화면은 문서가 바뀌면 key 로 새로 그려져 이 부품도 내려가지만 폼(FormProvider)은 key 바깥이라 이어진다.
  // 그래서 기준은 이 부품의 docId 도, 부품이 떠 있는지도 아니고 폼의 docId(문서 로드 때 reset 으로 들어온다)다 —
  // 부품 기준으로 보면 내려간 부품의 「다시 시도」가 새 문서 폼에 붙였고(재QA 1차), 떠 있는지로 보면 다른 문서에
  // 갔다가 같은 문서로 돌아온 경우까지 막혀 서버엔 연결됐는데 화면엔 안 보였다(재QA 2차)
  const isFormShowing = (id: string) => getValues('docId') === id;

  const [isHover, setIsHover] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(isRecordingInProgress && isHover);
  }, [isRecordingInProgress, isHover]);

  useEffect(() => {
    // recordDocId — 이 녹음을 한 문서. 연결 API 는 늘 이 문서로 보낸다
    const updatedRecordingData = async (
      item: Recording,
      recordDocId: string,
    ) => {
      // 중복 체크를 API 호출 전으로 이동
      const currentData = getValues('recordings');
      if (currentData.some((res) => res.uuid === item.uuid)) {
        return;
      }
      // 다른 부품이 이미 연결하는 중이면 그 약속을 같이 기다린다 — PUT 은 그쪽이, 폼 반영은 각자
      const shared = attachingUuids.get(item.uuid);
      const isAttacher = !shared;
      const attach =
        shared ??
        useSetAudioFile(recordDocId, {
          uuid: item.uuid,
          name: item.name,
          duration: item.duration,
        }).then(() => undefined);
      if (isAttacher) attachingUuids.set(item.uuid, attach);

      try {
        if (isAttacher) {
          // 업로드 중 상태를 함수형 업데이트로 변경 (stale closure 방지)
          setUploading((prev) => {
            // 이미 업로드 중인지 체크 (race condition 방지)
            if (prev.record.some((res) => res.name === item.name)) {
              return prev;
            }

            // 전송은 이미 끝났고 문서 연결만 남은 단계 → percent 없이 스피너만
            return {
              ...prev,
              record: [...prev.record, { name: item.name }],
            };
          });
        }

        await attach;
        if (isFormShowing(recordDocId)) {
          // 연결하는 사이 목록이 바뀌었을 수 있어 최신 값에 붙인다
          const latest = getValues('recordings');
          if (!latest.some((res) => res.uuid === item.uuid)) {
            setValue('recordings', [...latest, item], { shouldValidate: true });
          }
        }
        if (isAttacher) setStateInit(item.uuid);
      } catch (e) {
        // 실패 보고·안내·재시도는 PUT 을 보낸 부품 하나만 — 기다리던 부품은 조용히 빠진다
        // (재시도가 성공하면 그 부품의 폼에만 붙고, 다른 폼은 다음 새로고침에서 받는다)
        if (!isAttacher) return;
        captureRecordError('attach', e, {
          docId: recordDocId,
          title: item.name,
          uuid: item.uuid,
          duration: item.duration,
        });
        // 파일 전송은 끝났고 문서 연결만 실패한 상태 — 조용히 넘기면 사용자는
        // 저장된 줄 알지만 문서엔 녹음이 없다. 재시도는 업로드를 다시 하지 않고
        // 연결만 다시 시도한다(uuid 는 서버에 이미 있음).
        dialog.show(
          attachFailedDialogOptions({
            uuid: item.uuid,
            onRetry: () => updatedRecordingData(item, recordDocId),
            // 포기하면 녹음 상태를 비운다 — 남겨 두면 다음에 여는 문서가 이 녹음을
            // 자기 문서에 연결하려 했다 (PORTAL-12588 QA HIGH)
            onGiveUp: () => {
              setStateInit(item.uuid);
              showError({ message: t(ATTACH_GIVEN_UP_MESSAGE) });
            },
          }),
        );
      } finally {
        if (isAttacher) {
          attachingUuids.delete(item.uuid);
          setUploading((prev) => ({
            ...prev,
            record: prev.record.filter((res) => res.name !== item.name),
          }));
        }
      }
    };

    if (
      state.recordingData &&
      // 녹음한 문서에만 연결한다 — 다른 문서 화면이 이 녹음을 가져가지 않게 (PORTAL-12588)
      state.docId === docId &&
      !getValues('recordings')
        .map((res) => res.uuid)
        .includes(state.recordingData.uuid)
    ) {
      updatedRecordingData(state.recordingData, state.docId);
    }
  }, [state.recordingData, docId]);

  const onClickStart = async () => {
    const isFirstStart = !isPausedRecording && !isRecordingInProgress;
    // 다른 탭에서 녹음 중이라 시작하지 못했으면 첫 시작 처리도 하지 않는다 (PORTAL-12586)
    const started = await start(docId);
    if (started && onFirstStart && isFirstStart) {
      onFirstStart();
    }
  };

  return (
    <Container
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      isRecording={isRecordingInProgress}
      style={style}
      disabled={disabled}
    >
      <>
        {disabled ? (
          <Tooltip
            title={t('다른 문서에서 녹음 중이에요.')}
            arrow
            placement={'top-end'}
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: 'rgba(0, 0, 0, 1)',
                  color: '#FFF',
                  fontSize: '11px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '20px',
                  whiteSpace: 'nowrap',
                  '& .MuiTooltip-arrow': {
                    color: 'var(--dk-text, rgba(0, 0, 0, 1))',
                  },
                },
              },
            }}
          >
            <SvgBoxButton
              disabled
              style={{
                background: 'var(--dk-bg-subtle, #F9FAFB)',
                margin: '6px',
              }}
            >
              <RecordIcon
                style={{ color: 'var(--dk-text-disabled, #D1D8E2)' }}
              />
            </SvgBoxButton>
          </Tooltip>
        ) : state.isUploading ? (
          <div style={{ margin: '6px 12px' }}>
            <StyledTopLayout>
              <StyledText>
                {t('녹음 파일 업로드 중입니다.')} {state.uploadProgress}%
              </StyledText>
              <SpinnerLayout>
                <SpinningIcon />
              </SpinnerLayout>
            </StyledTopLayout>
            <StyledText style={{ marginTop: '4px' }}>
              {t('파일 업로드가 끝날 때까지 화면을 열어두세요.')}
            </StyledText>
          </div>
        ) : (
          <>
            {open ? (
              <div style={{ margin: '6px 12px' }}>
                <RecordingContainer>
                  <SvgBoxButton disabled={true}>
                    {isPausedRecording ? (
                      <AudioPlaceholder />
                    ) : (
                      <CustomVoiceVisualizer controls={controls} />
                    )}
                  </SvgBoxButton>
                  <StyledTimer>{formatAudioTime2(recordingTime)}</StyledTimer>
                  <SvgBoxButton onClick={pauseResume}>
                    {isPausedRecording ? <PlayIcon /> : <PauseIcon />}
                  </SvgBoxButton>
                  <SvgBoxButton onClick={stop}>
                    <StopIcon />
                  </SvgBoxButton>
                </RecordingContainer>
                <StyledText style={{ marginTop: '4px' }}>
                  {t('파일 업로드가 끝날 때까지 화면을 열어두세요.')}
                </StyledText>
              </div>
            ) : (
              <SvgBoxButton onClick={onClickStart} style={{ margin: '6px' }}>
                {isPausedRecording ? (
                  <AudioPlaceholder />
                ) : isRecordingInProgress ? (
                  <CustomVoiceVisualizer controls={controls} />
                ) : (
                  <RecordIcon style={{ color: '#C77AC4' }} />
                )}
              </SvgBoxButton>
            )}
          </>
        )}
      </>
    </Container>
  );
};

export default VoiceRecord2;
